/**
 * @fileoverview Render the calendar's filter bar (category, assignee, client, case, event type, responsibility and the AI-proposal toggle) as a controlled view. `Calendar.js` owns the filter state, keeps it in browser storage and applies it client-side to loaded events and AI proposals, so filtering never changes what the backend returns or any event record.
 */

import React, { useState, useMemo } from 'react';
import { Dropdown } from '../shared/AppDropdown';
import Button from "../shared/AppButton";
import Chip from '../shared/AppChip';
import { InputSwitch } from '../shared/AppToggle';
import AppIcon from "../shared/AppIcon";

const EVENT_TYPES = [
  { id: 'soft-deadline', name: 'Soft Deadline', icon: 'pi pi-clock' },
  { id: 'hard-deadline', name: 'Hard Deadline', icon: 'pi pi-exclamation-circle' }
];



function Filters({
  categoryOptions,
  assignees,
  cases,
  clients,
  filters,
  onFilterChange,
  onClearAll,
  filteredEventsCount,
  totalEventsCount,
  isLoading,
  showAiDeadlines,
  aiDeadlinesCount,
  onAiDeadlinesToggle
}) {
  // Start collapsed by default to keep the calendar view clean and focused
  const [isExpanded, setIsExpanded] = useState(false);

  // Enhanced option templates with icons for better visual scanning
  const assigneeOptionTemplate =

  (option) => {
    if (!option) return null;
    return (
      <div className="filter-option-item">
        <AppIcon icon="pi pi-user filter-option-icon" />
        <span>{option.label}</span>
      </div>);

  };

  const caseOptionTemplate =

  (option) => {
    if (!option) return null;
    return (
      <div className="filter-option-item">
        <AppIcon icon="pi pi-folder filter-option-icon" />
        <span>{option.label}</span>
      </div>);

  };

  const categoryOptionTemplate =

  (option) => {
    if (!option) return null;
    return (
      <div className="filter-option-item">
        <AppIcon icon="pi pi-tag filter-option-icon" />
        <span>{option.label}</span>
      </div>);

  };

  const eventTypeOptionTemplate =

  (option) => {
    if (!option) return null;
    const eventType = EVENT_TYPES.find((t) => t.id === option.value);
    return (
      <div className="filter-option-item">
        <AppIcon icon={`${eventType?.icon || 'pi pi-calendar'} filter-option-icon`} />
        <span>{option.label}</span>
      </div>);

  };

  const responsibilityOptionTemplate =

  (option) => {
    if (!option) return null;
    return (
      <div className="filter-option-item">
        <AppIcon icon="pi pi-check-circle filter-option-icon" />
        <span>{option.label}</span>
      </div>);

  };

  const clientOptionTemplate =

  (option) => {
    if (!option) return null;
    return (
      <div className="filter-option-item">
        <AppIcon icon="pi pi-building filter-option-icon" />
        <span>{option.label}</span>
      </div>);

  };

  const assigneeOptions = useMemo(
    () => (assignees || []).map((a) => ({ label: a.name, value: String(a.id) })),
    [assignees]
  );
  const caseOptions = useMemo(
    () => (cases || []).map((c) => ({ label: c.name, value: c.id })),
    [cases]
  );
  const clientOptions = useMemo(
    () => (clients || []).map((c) => ({ label: c.name, value: String(c.id) })),
    [clients]
  );
  const eventTypeOptions = useMemo(
    () => EVENT_TYPES.map((t) => ({ label: t.name, value: t.id })),
    []
  );
  const responsibilityOptions = useMemo(() => [
  { label: 'All', value: '' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Unconfirmed', value: 'unconfirmed' }], []);


  // Calculate active filters for chips display
  const activeFilters = useMemo(

  () => {
    const active = [];

    if (filters.category) {
      const option = categoryOptions?.find((c) => c.value === filters.category);
      active.push({
        key: 'category',
        label: option?.label || filters.category,
        icon: 'pi pi-tag'
      });
    }

    if (filters.assignee) {
      let label = filters.assignee;
      if (filters.assignee === 'unassigned') {
        label = 'No Assignee';
      } else {
        const option = assigneeOptions.find((a) => a.value === filters.assignee);
        label = option?.label || filters.assignee;
      }
      active.push({
        key: 'assignee',
        label,
        icon: 'pi pi-user'
      });
    }

    if (filters.client) {
      const option = clientOptions.find((c) => c.value === filters.client);
      active.push({
        key: 'client',
        label: option?.label || filters.client,
        icon: 'pi pi-building'
      });
    }

    if (filters.case) {
      const option = caseOptions.find((c) => c.value === filters.case);
      active.push({
        key: 'case',
        label: option?.label || filters.case,
        icon: 'pi pi-folder'
      });
    }

    if (filters.eventType) {
      const option = eventTypeOptions.find((t) => t.value === filters.eventType);
      active.push({
        key: 'eventType',
        label: option?.label || filters.eventType,
        icon: 'pi pi-calendar'
      });
    }

    if (filters.responsibility) {
      const option = responsibilityOptions.find((r) => r.value === filters.responsibility);
      active.push({
        key: 'responsibility',
        label: option?.label || filters.responsibility,
        icon: 'pi pi-check-circle'
      });
    }

    return active;
  }, [filters, categoryOptions, assigneeOptions, caseOptions, clientOptions, eventTypeOptions, responsibilityOptions]);


  const handleRemoveFilter =

  (filterKey) => {
    onFilterChange(filterKey, '');
  };

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <div
      className="filters-wrapper"
      data-expanded={isExpanded ? 'true' : 'false'}
      data-has-active-filters={hasActiveFilters ? 'true' : 'false'}
    >
      <div className="filters-header">
        <div className="filters-title-section">
          <AppIcon icon="pi pi-filter filters-icon" />
          <h2 className="filters-title">Filters</h2>
          {hasActiveFilters &&
          <span className="filter-count-badge" aria-label={`${activeFilters.length} filters active`}>
              {activeFilters.length}
            </span>}

        </div>
        
        <div className="filters-actions">
          {filteredEventsCount !== undefined && totalEventsCount !== undefined &&
          <span className="filter-results-count" aria-live="polite">
              {filteredEventsCount} / {totalEventsCount} events
            </span>}

          
          {hasActiveFilters &&
          <Button
            type="button"
            label="Clear"
            icon="pi pi-times"
            className="p-button-text p-button-sm clear-filters-btn-header"
            onClick={onClearAll}
            aria-label="Clear all active filters" />}


          
          <Button
            type="button"
            icon={isExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
            className="p-button-text p-button-sm filters-toggle-btn"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? "Collapse filters" : "Expand filters"}
            aria-expanded={isExpanded} />

        </div>
      </div>

      {isExpanded &&
      <div className="filters-content">
          {/* Active Filter Chips - shows visual indicators of what's currently filtered */}
          {hasActiveFilters &&
        <div className="active-filters-chips" role="region" aria-label="Active filters">
              {activeFilters.map((filter) =>
          <Chip
            key={filter.key}
            label={filter.label}
            icon={filter.icon}
            removable
            onRemove={() => handleRemoveFilter(filter.key)}
            className="filter-chip"
            aria-label={`Remove ${filter.label} filter`} />

          )}
            </div>}


          {/* Filter Controls Grid */}
          <div className="filters-grid">
            <div className="filter-group" data-filter-active={!!filters.category}>
              <label htmlFor="category-filter" className="filter-label">
                <AppIcon icon="pi pi-tag filter-label-icon" />
                CATEGORY
              </label>
              <Dropdown
              id="category-filter"
              value={filters.category}
              options={[{ label: 'All Categories', value: '' }, ...(categoryOptions || [])]}
              onChange={(e) => onFilterChange('category', e.value)}
              filter
              showClear
              placeholder="All Categories"
              itemTemplate={categoryOptionTemplate}
              disabled={isLoading}
              className={filters.category ? 'filter-active' : ''}
              aria-label="Filter by category" />

            </div>

            <div className="filter-group" data-filter-active={!!filters.assignee}>
              <label htmlFor="assignee-filter" className="filter-label">
                <AppIcon icon="pi pi-user filter-label-icon" />
                ASSIGNEE
              </label>
              <Dropdown
              id="assignee-filter"
              value={filters.assignee}
              options={[
              { label: 'All Assignees', value: '' },
              { label: 'No Assignee', value: 'unassigned' },
              ...assigneeOptions]}

              onChange={(e) => onFilterChange('assignee', e.value)}
              filter
              showClear
              placeholder="All Assignees"
              itemTemplate={assigneeOptionTemplate}
              disabled={isLoading}
              className={filters.assignee ? 'filter-active' : ''}
              aria-label="Filter by assignee" />

            </div>

            <div className="filter-group" data-filter-active={!!filters.client}>
              <label htmlFor="client-filter" className="filter-label">
                <AppIcon icon="pi pi-building filter-label-icon" />
                CLIENT
              </label>
              <Dropdown
              id="client-filter"
              value={filters.client}
              options={[{ label: 'All Clients', value: '' }, ...clientOptions]}
              onChange={(e) => onFilterChange('client', e.value)}
              filter
              showClear
              placeholder="All Clients"
              itemTemplate={clientOptionTemplate}
              disabled={isLoading}
              className={filters.client ? 'filter-active' : ''}
              aria-label="Filter by client" />

            </div>

            <div className="filter-group" data-filter-active={!!filters.case}>
              <label htmlFor="case-filter" className="filter-label">
                <AppIcon icon="pi pi-folder filter-label-icon" />
                CASE
              </label>
              <Dropdown
              id="case-filter"
              value={filters.case}
              options={[{ label: 'All Cases', value: '' }, ...caseOptions]}
              onChange={(e) => onFilterChange('case', e.value)}
              filter
              showClear
              placeholder="All Cases"
              itemTemplate={caseOptionTemplate}
              disabled={isLoading}
              className={filters.case ? 'filter-active' : ''}
              aria-label="Filter by case" />

            </div>

            <div className="filter-group" data-filter-active={!!filters.eventType}>
              <label htmlFor="event-type-filter" className="filter-label">
                <AppIcon icon="pi pi-calendar filter-label-icon" />
                EVENT TYPE
              </label>
              <Dropdown
              id="event-type-filter"
              value={filters.eventType}
              options={[{ label: 'All Event Types', value: '' }, ...eventTypeOptions]}
              onChange={(e) => onFilterChange('eventType', e.value)}
              filter
              showClear
              placeholder="All Event Types"
              itemTemplate={eventTypeOptionTemplate}
              disabled={isLoading}
              className={filters.eventType ? 'filter-active' : ''}
              aria-label="Filter by event type" />

            </div>

            <div className="filter-group" data-filter-active={!!filters.responsibility}>
              <label htmlFor="responsibility-filter" className="filter-label">
                <AppIcon icon="pi pi-check-circle filter-label-icon" />
                STATUS
              </label>
              <Dropdown
              id="responsibility-filter"
              value={filters.responsibility}
              options={responsibilityOptions}
              onChange={(e) => onFilterChange('responsibility', e.value)}
              showClear
              placeholder="All"
              itemTemplate={responsibilityOptionTemplate}
              disabled={isLoading}
              className={filters.responsibility ? 'filter-active' : ''}
              aria-label="Filter by responsibility status" />

            </div>

            {/* AI Deadlines Toggle - Shows/hides AI-detected deadlines from court documents */}
            <div className="filter-group filter-group-toggle" data-filter-active={showAiDeadlines}>
              <label htmlFor="ai-deadlines-toggle" className="filter-label">
                <AppIcon icon="pi pi-bolt filter-label-icon" />
                AI DEADLINES
                {aiDeadlinesCount !== undefined &&
              <span className="filter-count-indicator">({aiDeadlinesCount})</span>}

              </label>
              <div className="toggle-control">
                <InputSwitch
                id="ai-deadlines-toggle"
                checked={showAiDeadlines}
                onChange={(e) => onAiDeadlinesToggle && onAiDeadlinesToggle(e.value)}
                disabled={isLoading}
                aria-label="Toggle AI-detected deadlines visibility" />

                <span className="toggle-label-text">
                  {showAiDeadlines ? 'Showing' : 'Hidden'}
                </span>
              </div>
            </div>
          </div>

        </div>}

    </div>);

}

export default Filters;
